/** Config transforms for entering / resizing lifestyle mode.
 *
 *  Two problems these solve, both reported after round 1:
 *
 *  D3 — enabling the mode left every element switched on and crammed into half
 *  the width. The operator's first sight of lifestyle mode was a wreck.
 *
 *  D4 — worse, the elements that fell OUTSIDE the product half could not be
 *  recovered. The default logo sits at x=880; with a 540px half its drag handle
 *  is off-canvas, so "just reposition it" is impossible — there is nothing to
 *  grab. An element the operator cannot reach is a dead end.
 *
 *  Both are pure transforms over `elements` so they can be tested directly and
 *  reused by any caller that changes the split.
 */
import { CANVAS_SIZE } from './canvas-coords';
import { ELEMENT_BOXES, HIDEABLE_KEYS, customElementBox } from './elementBoxes';
import type { DesignConfig, LifestyleElement } from '../api/types';
import { isCustomElement, resolveProductImageBox, splitWidths } from '../api/types';

type Elements = DesignConfig['elements'];
type Bag = Record<string, Record<string, unknown>>;

/** Clamp one box into `[0, limit - size]`, never returning a negative.
 *  A handle wider than the half clamps to 0 rather than off the left edge. */
function clamp(value: number, size: number, limit: number): number {
  return Math.max(0, Math.min(Math.max(0, limit - size), Math.round(value)));
}

/** Move every visible, positioned element inside the product half.
 *
 *  Applied on enable AND whenever the half shrinks, because a ratio bump has
 *  exactly the same stranding effect as switching the mode on.
 */
export function confineToProductHalf(elements: Elements, ratio: number): Elements {
  const [productW] = splitWidths(CANVAS_SIZE, ratio);
  const bag = { ...(elements as unknown as Bag) };

  for (const [key, raw] of Object.entries(bag)) {
    if (key === 'lifestyle' || key === 'background') continue;
    const el = raw as Record<string, unknown>;
    if (typeof el?.x !== 'number' || typeof el?.y !== 'number') continue;
    // Hidden elements keep their coordinates: re-enabling one should put it
    // back where the operator left it, not somewhere the clamp invented.
    if (el.visible === false) continue;

    let box: { w: number; h: number };
    if (key === 'product_image') {
      const b = resolveProductImageBox(elements.product_image, productW);
      box = { w: b.w, h: b.h };
    } else if (ELEMENT_BOXES[key]) {
      box = ELEMENT_BOXES[key];
    } else if (isCustomElement(raw as never)) {
      box = customElementBox(el as never);
    } else {
      continue;
    }

    const x = clamp(el.x, box.w, productW);
    const y = clamp(el.y, box.h, CANVAS_SIZE);
    if (x !== el.x || y !== el.y) bag[key] = { ...el, x, y };
  }
  return bag as unknown as Elements;
}

/** Turn lifestyle mode on: write the element, hide the rest, tidy what's left.
 *
 *  Hiding is `visible: false`, never a delete — the operator's colours and
 *  positions survive, so re-enabling a badge restores it exactly. They opt each
 *  element back IN, which is the only way to get a legible half at 540px.
 */
export function enableLifestyle(
  elements: Elements,
  lifestyle: LifestyleElement,
): Elements {
  const bag = { ...(elements as unknown as Bag) };
  for (const key of HIDEABLE_KEYS) {
    if (bag[key]) bag[key] = { ...bag[key], visible: false };
  }
  for (const [key, raw] of Object.entries(bag)) {
    if (isCustomElement(raw as never)) bag[key] = { ...raw, visible: false };
  }
  bag.lifestyle = lifestyle as unknown as Record<string, unknown>;
  return confineToProductHalf(bag as unknown as Elements, lifestyle.ratio);
}

/** Turn lifestyle mode off and put the composition back the way it was.
 *
 *  `snapshot` is the bag as it stood immediately BEFORE the mode was switched
 *  on, which makes the normal path an exact inverse of enableLifestyle: the
 *  auto-hide and the clamp are undone together. That is what lets the panel
 *  offer a plain "go back to single layout" instead of round 2's caveat that
 *  switching off kept half-space positions — the operator was left with a
 *  full-width canvas and every badge bunched into the left 540px.
 *
 *  A null snapshot means the config was ALREADY in lifestyle mode when the
 *  editor loaded it, so there is no earlier state to rewind to. Coordinates are
 *  unrecoverable — they were never stored — but the overlays are still switched
 *  back on: handing back a canvas holding nothing but the product image would
 *  be a worse surprise than positions the operator can see and drag.
 */
export function disableLifestyle(
  elements: Elements,
  snapshot: Elements | null,
): Elements {
  const bag = { ...((snapshot ?? elements) as unknown as Bag) };
  delete bag.lifestyle;
  if (snapshot) return bag as unknown as Elements;

  for (const key of HIDEABLE_KEYS) {
    if (bag[key]) bag[key] = { ...bag[key], visible: true };
  }
  for (const [key, raw] of Object.entries(bag)) {
    if (isCustomElement(raw as never)) bag[key] = { ...raw, visible: true };
  }
  return bag as unknown as Elements;
}
